/**
 * The alert itself: one 80 px strip across the top of the page.
 *
 * Severity and urgency are the worst in force across the settlements the run
 * reached; the four key figures beside them are the ones an operator reads
 * first - people in the alerted settlements, the shortest lead time, the
 * deepest water and the number of dispatches the chain carries.
 */

import { SEVERITY_COLOUR, istTime } from './shared'
import type { Evaluation, SettlementRow } from './shared'

interface Props {
  evaluation: Evaluation
  alerted: SettlementRow[]
  worst: string
  filter: SettlementRow | null
}

function Figure({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="flex min-w-0 flex-col justify-center">
      <div className="section-label truncate">{label}</div>
      <div className="flex items-baseline gap-1">
        <span
          className="font-mono tabular-nums"
          style={{ fontSize: 'var(--fs-32)', lineHeight: 1.05, color: 'var(--ink-text)' }}
        >
          {value}
        </span>
        {unit && (
          <span style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>{unit}</span>
        )}
      </div>
    </div>
  )
}

export function AlertStrip({ evaluation, alerted, worst, filter }: Props) {
  const reached = alerted.filter((s) => s.arrival_time_min !== null)
  const rows = filter ? [filter] : reached.length ? reached : alerted
  const colour = SEVERITY_COLOUR[worst]
  const urgency =
    rows.map((s) => s.alert.urgency).find((u) => u === 'Immediate') ??
    rows.map((s) => s.alert.urgency).find((u) => u === 'Expected') ??
    rows[0]?.alert.urgency ??
    ''
  const population = rows.reduce((sum, s) => sum + (s.population ?? 0), 0)
  const hasPopulation = rows.some((s) => s.population !== null)
  const reach = evaluation.location_kind === 'crossing'
  const leads = rows
    .map((s) => s.alert.lead_time_min)
    .filter((v): v is number => v !== null)
  const depth = Math.max(...rows.map((s) => s.max_depth_m))
  const ids = new Set(rows.map((s) => s.settlement_id))
  const dispatches = evaluation.events.filter((e) => ids.has(e.settlement_id)).length

  return (
    <section
      className="flex items-stretch gap-5 px-4"
      style={{
        minHeight: 88,
        background: 'var(--ink-surface)',
        borderTop: `3px solid ${colour}`,
        borderBottom: '1px solid var(--ink-border)',
      }}
      data-testid="alert-strip"
    >
      <div className="flex min-w-0 flex-col justify-center" style={{ width: 210 }}>
        <div className="section-label">Severity · urgency</div>
        <div className="flex items-baseline gap-2">
          <span
            className="font-semibold"
            style={{ fontSize: 'var(--fs-32)', lineHeight: 1.05, color: colour }}
          >
            {worst.toUpperCase()}
          </span>
          <span style={{ fontSize: 'var(--fs-cell)', color: 'var(--ink-text-dim)' }}>
            {urgency.toUpperCase()}
          </span>
        </div>
      </div>

      <div
        className="flex min-w-0 flex-col justify-center"
        style={{ width: 300, borderLeft: '1px solid var(--ink-border)', paddingLeft: 20 }}
      >
        <div className="section-label">Event</div>
        <div className="truncate" style={{ fontSize: 'var(--fs-body)', color: 'var(--ink-text)' }}>
          {reach ? 'Glacial lake outburst flood' : 'Dam-break flood'} · {rows.map((s) => s.name).join(', ')}
        </div>
        <div
          className="truncate font-mono"
          style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}
        >
          effective {istTime(evaluation.exercise_start)} · expires{' '}
          {istTime(
            new Date(
              new Date(evaluation.exercise_start).getTime() +
                evaluation.run.duration_min * 60_000,
            ).toISOString(),
          )}
        </div>
      </div>

      <div
        className="grid flex-1 grid-cols-4 gap-5"
        style={{ borderLeft: '1px solid var(--ink-border)', paddingLeft: 20 }}
      >
        {hasPopulation ? (
          <Figure label="Population" value={population.toLocaleString('en-IN')} />
        ) : (
          <Figure label="Locations" value={String(rows.length)} />
        )}
        <Figure
          label="Shortest lead"
          value={String(Math.floor(Math.min(...leads) / 60))}
          unit={`h ${String(Math.round(Math.min(...leads) % 60)).padStart(2, '0')} min`}
        />
        <Figure label="Max depth" value={depth.toFixed(2)} unit="m" />
        <Figure label="Dispatches" value={String(dispatches)} />
      </div>
    </section>
  )
}
